import { iconSet } from '@/components/ui/Icon/IconLibrary';
import { NavigationLink, NavigationSection, NavigationSections } from '@/types/navigation';
import { IconChevronDown, IconChevronLeft } from '@tabler/icons-react';

interface SubMenuProps {
  name: string;
  content: NavigationSections;
  open: boolean;
  onEnter: () => void;
  onClick: () => void;
  onBack: () => void;
}

const host = process.env.NEXT_PUBLIC_HOST_NAME;

export const SubMenu = ({ name, content, open, onEnter, onClick, onBack }: SubMenuProps) => {
  return (
    <div role="none" onMouseEnter={onEnter}>
      <Toggle name={name} open={open} onClick={onClick} />

      <span
        id={name}
        className={`bg-neutral absolute inset-0 z-30 mt-[1px] transform overflow-scroll pb-16 opacity-100 transition-all duration-300 ease-in-out md:right-0 md:left-0 lg:top-full lg:right-0 lg:bottom-auto lg:left-0 lg:transform-none lg:p-2 lg:transition-none ${open ? 'pointer-events-auto translate-x-0 opacity-100' : 'pointer-events-none hidden translate-x-full opacity-0'}`}
        role="menu"
      >
        {/* Submenu Content Container */}
        <div className="tc-container bg-neutral flex w-full flex-col items-start justify-start gap-4 py-4 pb-16 text-left lg:flex-row lg:py-8">
          <MobileBackButton onClick={onBack} />

          {content.map((section, i) => (
            <Section section={section} key={i} />
          ))}
        </div>
      </span>
    </div>
  );
};

const MobileBackButton = ({ onClick }: Pick<SubMenuProps, 'onClick'>) => (
  <button onClick={onClick} className="mb-4 -ml-2 flex items-center text-[16px] font-medium lg:hidden" role="menuitem">
    <IconChevronLeft /> Back to main menu
  </button>
);

const Toggle = ({ name, open, onClick }: Pick<SubMenuProps, 'name' | 'open' | 'onClick'>) => (
  <button type="button" aria-haspopup="true" aria-controls={name} className="flex w-full" onClick={onClick}>
    <span className="flex w-full justify-between border-b-[1px] border-b-[#B1ADC0] pb-4 lg:border-0 lg:px-2 lg:pb-0">
      <span className="text-dark-200 font-medium">{name}</span>
      <span className="ml-2 flex items-center justify-center">
        <IconChevronDown
          size={22}
          stroke={3}
          className={`ease-all -rotate-90 transition-all ${open ? 'rotate-180' : 'lg:rotate-0'}`}
        />
      </span>
    </span>
  </button>
);

const Section = ({ section: { url, title, icon, links } }: { section: NavigationSection }) => {
  const Icon = icon ? iconSet[icon] : null;

  return (
    <div className="mb-4 flex w-full flex-col items-start justify-start text-left lg:max-w-1/2">
      <span className="text-dark-400 group mb-3 flex w-full border-b-[1px] border-b-[#B1ADC0] pb-1 font-semibold">
        {Icon && (
          <span className="stroke-dark -mt-[2px] mr-2 flex items-center justify-center">
            <Icon name={icon} size={18} width={18} height={18} />
          </span>
        )}
        {url ? (
          <a
            className="group-hover:text-primary text-dark-400 w-full cursor-pointer py-2 font-semibold underline-offset-8 group-hover:font-semibold group-hover:underline"
            href={`${host}${url}`}
            role="menuitem"
          >
            {title}
          </a>
        ) : (
          <span className="text-dark-400 w-full py-2 font-semibold underline-offset-8">{title}</span>
        )}
      </span>

      {links.map((item, i) => (
        <MenuItem key={i} item={item} />
      ))}
    </div>
  );
};

const MenuItem = ({ item: { url, title, newWindow } }: { item: NavigationLink }) => (
  <a
    className="hover:text-primary text-dark-200 w-full py-2 font-medium underline-offset-8 hover:font-semibold hover:underline"
    href={`${host}${url}`}
    target={newWindow ? '_blank' : undefined}
    rel={newWindow ? 'noopener noreferrer' : undefined}
    role="menuitem"
  >
    {title}
  </a>
);
